import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import {
    EmbeddedApiState,
    ReceivedMessageState,
    SentMessageState,
} from 'embedded/api/store/EmbeddedApiState';
import orderBy from 'lodash/orderBy';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import './SupportVisualisationEmbeddedApiMessages.scss';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { Button } from '@instinet-analytics/semantic-ui-components';

type DirectionFilter = 'All' | 'In' | 'Out';

export default function SupportVisualisationEmbeddedApiMessages(
    props: VisualisationContextProps
) {
    const { state, height, width } = props;
    const [detailView, setDetailView] = useState<GridRow | undefined>(
        undefined
    );
    const [direction, setDirection] = useState<DirectionFilter>('All');

    const style = { height, width };

    const embeddedApiState = state?.state.embeddedApi;

    const gridData = getGridData(embeddedApiState, direction);

    const searchPanelHeight = 40;
    const searchPanelStyle = { height: searchPanelHeight };
    const messagesPanelStyle = { height: height - searchPanelHeight };

    const onSelectedRowIdsChanged = (rowDataIds: string[]) => {
        if (rowDataIds.length === 1) {
            const row = gridData.find(
                (r) => r.seq.toString() === rowDataIds[0]
            );
            setDetailView(row);
        }
    };

    return (
        <div
            className={'SupportVisualisationEmbeddedApiMessages info'}
            style={style}
        >
            <div className="search-panel" style={searchPanelStyle}>
                <div className="title">Direction:</div>
                <div>
                    <Button
                        className={
                            direction === 'All' ? 'primary' : 'Secondary'
                        }
                        onClick={() => setDirection('All')}
                    >
                        All
                    </Button>{' '}
                    <Button
                        className={direction === 'In' ? 'primary' : 'Secondary'}
                        onClick={() => setDirection('In')}
                    >
                        In
                    </Button>{' '}
                    <Button
                        className={
                            direction === 'Out' ? 'primary' : 'Secondary'
                        }
                        onClick={() => setDirection('Out')}
                    >
                        Out
                    </Button>
                </div>
            </div>
            <div className="message-panel" style={messagesPanelStyle}>
                <WidgetPanelGrid
                    data={gridData}
                    colDefs={getColDefs()}
                    csvFileName={'embeddedApiMessages'}
                    getRowDataId={(i) => i.seq.toString()}
                    onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                />
                {detailView && (
                    <div className="detail-view">
                        <JsonPanel data={detailView.message} />
                    </div>
                )}
            </div>
        </div>
    );
}

function getGridData(
    embeddedApiState: EmbeddedApiState | undefined,
    direction: DirectionFilter
): GridRow[] {
    if (!embeddedApiState) {
        return [];
    }

    const ret: GridRow[] = [];

    if (direction !== 'Out') {
        for (const topic of Object.keys(embeddedApiState.receivedMessages)) {
            for (const msg of embeddedApiState.receivedMessages[topic]) {
                ret.push({
                    direction: 'In',
                    topic,
                    seq: msg.seq,
                    uid: msg.uid,
                    status: msg.status,
                    time: msg.receivedTime,
                    payload: msg.payload,
                    message: msg,
                });
            }
        }
    }

    if (direction !== 'In') {
        for (const topic of Object.keys(embeddedApiState.sentMessages)) {
            for (const msg of embeddedApiState.sentMessages[topic]) {
                ret.push({
                    direction: 'Out',
                    topic,
                    seq: msg.seq,
                    uid: `${msg.identity.uuid}/${msg.identity.name}`,
                    status: msg.status,
                    time: msg.time,
                    payload: msg.message,
                    message: msg,
                });
            }
        }
    }

    let maxSeq = Math.max(...ret.map((r) => r.seq ?? -1));
    for (const r of ret) {
        if (r.seq === undefined) r.seq = ++maxSeq;
    }

    return orderBy(ret, ['time'], ['desc']);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPayload = any;

interface GridDataItem {
    seq: number;
    uid: string;
    topic: string;
    status: string;
    payload: AnyPayload;
    time: Date;
}

interface GridInRow extends GridDataItem {
    direction: 'In';
    message: ReceivedMessageState<unknown>;
}

interface GridOutRow extends GridDataItem {
    direction: 'Out';
    message: SentMessageState<unknown>;
}

type GridRow = GridInRow | GridOutRow;

function getColDefs(): ColDef[] {
    return ['time', 'topic', 'direction', 'status', 'uid'].map((key) => ({
        field: key,
        width: 120,
        resizable: true,
    }));
}
